/**
 * Доступ к Jira «через браузер» (расширение Lens для Jira).
 *
 * Когда режим доступа к Jira = extension (PAT в Jira отключён), запросы в Jira
 * выполняет браузер пользователя, а не сервер. Здесь — клиентская половина:
 *  - определить режим (`/api/jira/relay/config`);
 *  - убедиться, что расширение установлено (обмен через window.postMessage);
 *  - держать один SSE-канал к серверу на сессию, прокачивая запросы Jira в
 *    расширение и возвращая ответы серверу;
 *  - отдавать id канала общему API-клиенту, который добавляет заголовок
 *    X-Jira-Relay ко всем запросам.
 *
 * Расширение — тупой исполнитель: вся логика синхронизации на сервере.
 * См. docs/modules/jira-extension-transport.md.
 */
import { apiRequest, setJiraRelayChannelIdProvider } from '../../shared/api/client.ts';

const MARKER = '__lensJira';

export type JiraAccessMode = 'token' | 'extension';

let modeCache: JiraAccessMode | null = null;

/** Режим доступа к Jira. Кэшируется; при ошибке (напр. не залогинен) — token. */
export async function getJiraAccessMode(): Promise<JiraAccessMode> {
  if (modeCache) return modeCache;
  try {
    const r = await apiRequest<{ mode: JiraAccessMode }>('/api/jira/relay/config');
    modeCache = r.mode;
  } catch {
    return 'token'; // не кэшируем — вдруг просто ещё не залогинены
  }
  return modeCache;
}

// ─── обмен со страничным мостом расширения (window.postMessage) ──────────────

interface ExtResponse {
  ok: boolean;
  result?: { status: number; headers: Record<string, string>; body: string };
  error?: { message: string; code: string };
}

/** Установлено ли расширение (мост отвечает событием ready). */
export function pingExtension(timeoutMs = 1200): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window) return;
      const d = e.data;
      if (d && d[MARKER] === true && d.from === 'lens-ext' && d.kind === 'ready') {
        finish(true);
      }
    };
    const finish = (v: boolean) => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMsg);
      resolve(v);
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ [MARKER]: true, from: 'lens-page', kind: 'ping' }, window.location.origin);
    setTimeout(() => finish(false), timeoutMs);
  });
}

/** Отправить один запрос в расширение и дождаться сырого ответа Jira. */
function sendToExtension(
  payload: { url: string; method: string; body?: string; headers: Record<string, string> },
  timeoutMs = 30_000,
): Promise<ExtResponse> {
  return new Promise((resolve) => {
    const id = crypto.randomUUID();
    let done = false;
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window) return;
      const d = e.data;
      if (d && d[MARKER] === true && d.from === 'lens-ext' && d.id === id) {
        finish({ ok: d.ok, result: d.result, error: d.error });
      }
    };
    const finish = (r: ExtResponse) => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMsg);
      resolve(r);
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ [MARKER]: true, from: 'lens-page', kind: 'request', id, payload }, window.location.origin);
    setTimeout(() => finish({ ok: false, error: { message: 'Расширение не ответило', code: 'extension_timeout' } }), timeoutMs);
  });
}

// ─── постоянный канал к серверу (SSE) ────────────────────────────────────────

let source: EventSource | null = null;
let currentChannelId: string | null = null;
let starting = false;
let extensionPresent = false;

interface RelayRequestEvent {
  type: 'request';
  reqId: string;
  method: string;
  url: string;
  body: string | null;
  headers: Record<string, string>;
}

async function handleRequest(channelId: string, ev: RelayRequestEvent): Promise<void> {
  const resp = await sendToExtension({
    url: ev.url,
    method: ev.method,
    body: ev.body ?? undefined,
    headers: ev.headers ?? {},
  });
  // Возвращаем серверу ровно то, что вернул браузер (сырой ответ Jira или ошибку).
  try {
    await apiRequest(`/api/jira/relay/${channelId}/response`, {
      method: 'POST',
      body: { reqId: ev.reqId, ok: resp.ok, result: resp.result, error: resp.error },
    });
  } catch {
    // Сервер сам отвалит запрос по таймауту — не роняем прокачку.
  }
}

function openChannel(): void {
  if (source) return;
  const es = new EventSource('/api/jira/relay/stream', { withCredentials: true });
  source = es;
  es.onmessage = (e: MessageEvent) => {
    let msg: unknown;
    try { msg = JSON.parse(e.data); } catch { return; }
    if (!msg || typeof msg !== 'object') return;
    const m = msg as {
      type?: string;
      channelId?: string;
      reqId?: string;
      method?: string;
      url?: string;
      body?: string | null;
      headers?: Record<string, string>;
    };
    if (m.type === 'open' && typeof m.channelId === 'string') {
      currentChannelId = m.channelId;
      return;
    }
    if (m.type === 'request' && currentChannelId) {
      void handleRequest(currentChannelId, m as unknown as RelayRequestEvent);
    }
  };
  es.onerror = () => {
    // Соединение упало — id недействителен, пока не придёт новый 'open'.
    // EventSource переподключится сам.
    currentChannelId = null;
  };
}

function closeChannel(): void {
  if (source) {
    source.close();
    source = null;
  }
  currentChannelId = null;
}

/**
 * Поднять доступ «через браузер», если это нужно. Идемпотентно. Вызывается, когда
 * пользователь авторизован. В token-режиме ничего не делает.
 */
export async function initJiraExtensionRelay(): Promise<void> {
  if (starting || source) return;
  starting = true;
  try {
    const mode = await getJiraAccessMode();
    if (mode !== 'extension') return;
    extensionPresent = await pingExtension();
    // id канала клиент подставит в заголовок только когда канал реально открыт.
    setJiraRelayChannelIdProvider(() => currentChannelId);
    if (extensionPresent) {
      openChannel();
    }
  } finally {
    starting = false;
  }
}

/** Погасить канал (при выходе из аккаунта). */
export function teardownJiraExtensionRelay(): void {
  closeChannel();
  setJiraRelayChannelIdProvider(null);
  modeCache = null;
  extensionPresent = false;
}

/** Статус для UI: режим, установлено ли расширение, открыт ли канал. */
export function getJiraRelayStatus(): { mode: JiraAccessMode | null; present: boolean; connected: boolean } {
  return { mode: modeCache, present: extensionPresent, connected: currentChannelId !== null };
}
